import { useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { LabAnalysisModel } from "@/Common/Models/ManualLabResultsEntry/LabAnalysisModel";
import { ObservationEntryFormData } from "@/Common/Models/ManualLabResultsEntry/ObservationEntryFromData";
import { Label } from "@/DesignSystem/Molecules/ToolBox/Label";
import { DateSelector } from "../Inputs/DateSelector";
import { TextAreaResizable } from "../Inputs/TextAreaResizable";
import { TextboxControl } from "../Inputs/TextboxControl";
import { TimeSelector } from "../Inputs/TimeSelector";
import { ObservationFlagDropdown } from "./ObservationFlagDropdown";
import { ResultCategoriesDropdown } from "./ResultCategoriesDropdown";
import { SearchLabAnalysisDropdown } from "./SearchLabAnalysisDropdown";

import styles from "./ObservationEntryForm.module.scss";

interface ObservationEntryFormProps {
    dataCy?: string;
    data: ObservationEntryFormData;
    onChange: (mutate: (p: ObservationEntryFormData) => void) => void;
}

function ObservationEntryForm({ dataCy, data, onChange }: Readonly<ObservationEntryFormProps>): JSX.Element {
    const { getText } = useTranslation();
    const [resultInputTouched, setResultInputTouched] = useState<boolean>(false);

    const handleDateTimeChange = (date: Date, type: "date" | "time"): void => {
        let nextDateTime = new Date(data.dateTime);

        if (type === "date") {
            nextDateTime.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        }
        else {
            nextDateTime.setHours(date.getHours(), date.getMinutes(), date.getSeconds());
        }

        onChange(p => p.dateTime = nextDateTime);
    };

    const handleAnalysisChange = (item: LabAnalysisModel | null): void => {
        let nextData = data;
        nextData.AnalysisModel = item;

        onChange(p => p.AnalysisModel = item);
    };

    return (
        <div className={styles.container} data-cy={dataCy}>
            <div
                className={styles.date}
            >
                <Label
                    label={getText("DateLabel")}
                    htmlFor="result-entry-date"
                />
                <DateSelector
                    id="result-entry-date"
                    dataCy="result-entry-date"
                    date={data.dateTime}
                    onChangeCallback={date => handleDateTimeChange(date, "date")}
                    dateFormat="yyyy-MM-dd"
                    disabled={false}
                    valid={!!data.dateTime}
                    placeholder="YYYY-MM-DD"
                />

            </div>
            <div className={styles.time}>
                <Label
                    label={getText("TimeLabel")}
                    htmlFor="result-entry-time"
                />
                <TimeSelector
                    id="result-entry-time"
                    dataCy="result-entry-time"
                    date={data.dateTime}
                    onChangeCallback={date => handleDateTimeChange(date, "time")}
                    dateFormat="HH:mm:ss"
                    disabled={false}
                    placeholder="hh:mm:ss"
                />
            </div>

            <div
                className={styles.category}
            >
                <Label
                    label={getText("ManualResults_CategoryLabel")}
                    htmlFor="result-category-dropdown"
                />
                <ResultCategoriesDropdown
                    id="result-category-dropdown"
                    onChange={value => onChange((p => p.categoryLookupId = value))}
                    categoryLookupId={data.categoryLookupId}
                    dataCy="result-category-dropdown"
                />
            </div>

            <div
                className={styles.analysis}
            >
                <Label
                    label={getText("ManualResults_AnalysisLabel")}
                    htmlFor="search-lab-analysis-dropdown"
                    required={true}
                />
                <SearchLabAnalysisDropdown
                    id="search-lab-analysis-dropdown"
                    dataCy="search-lab-analysis-dropdown"
                    onItemSelected={handleAnalysisChange}
                    item={data.AnalysisModel}
                />
            </div>

            <div
                className={styles.refLow}
            >
                <Label
                    label={getText("ManualResults_RefLowLabel")}
                    htmlFor="refLowInput"
                />
                <TextboxControl
                    value={data.referenceLow}
                    onChange={value => onChange((p => p.referenceLow = value))}
                    dataCy="refLow-input"
                    maxLength={10}
                    id="refLowInput"
                />
            </div>

            <div
                className={styles.refHigh}
            >
                <Label
                    label={getText("ManualResults_RefHighLabel")}
                    htmlFor="refHighInput"
                />
                <TextboxControl
                    value={data.referenceHigh}
                    onChange={value => onChange((p => p.referenceHigh = value))}
                    dataCy="refHigh-input"
                    maxLength={10}
                    id="refHighInput"
                />
            </div>

            <div
                className={styles.result}
            >
                <Label
                    label={getText("ResultLabel")}
                    htmlFor="resultInput"
                    required={true}
                />
                <TextboxControl
                    value={data.result}
                    onChange={value => {
                        setResultInputTouched(true);
                        onChange((p => p.result = value));
                    }}
                    dataCy="result-input"
                    maxLength={250}
                    id="resultInput"
                    valid={resultInputTouched ? !!data.result : true}
                />
            </div>

            <div
                className={styles.units}
            >
                <Label
                    label={getText("ManualResults_UnitsLabel")}
                    htmlFor="unitsInput"
                />
                <TextboxControl
                    value={data.units}
                    onChange={value => onChange((p => p.units = value))}
                    dataCy="units-input"
                    maxLength={20}
                    id="unitsInput"
                />
            </div>

            <div
                className={styles.flag}
            >
                <Label
                    label={getText("ManualResults_FlagLabel")}
                    htmlFor="flag-input"
                />
                <ObservationFlagDropdown
                    onChange={value => onChange((p => p.level = value))}
                    value={data.level}
                    dataCy="flag-input"
                    id="flag-input"
                />
            </div>

            <div
                className={styles.notes}
            >
                <Label
                    label={getText("NotesLabel")}
                    htmlFor="noteInput"
                />
                <TextAreaResizable
                    id="noteInput"
                    verticalResizeOnly={true}
                    text={data.note}
                    onChange={value => onChange((p => p.note = value))}
                    dataCy="note-input"
                    minHeightPx={73}
                    maxLength={250}
                />
            </div>
        </div>
    );
}

export { ObservationEntryForm };
